import { Component } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { isChunkLoadError, recoverFromChunkError } from '../../lib/chunkRecovery';

/**
 * Global error boundary so an unexpected render error (e.g. an object passed
 * to a React child, a broken integration response) shows a recovery UI
 * instead of a blank white screen.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // A page that could not be fetched because the deploy replaced its chunk is not a
    // fault to report — it is a stale browser, and one reload fixes it. This boundary
    // catches the throw before it reaches the window, so recovering has to happen here
    // (21 Sep 2026: pressing Fix on an alert gave him "Loading chunk 8963 failed").
    if (recoverFromChunkError(error)) return;
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, errorInfo);
    if (isChunkLoadError(error)) return;  // reload already spent; don't page Sentry for it
    // Report to Sentry with the React component stack (no-op if Sentry unset)
    Sentry.withScope((scope) => {
      scope.setContext('react', { componentStack: errorInfo?.componentStack });
      Sentry.captureException(error);
    });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleDismiss = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const stale = isChunkLoadError(this.state.error);

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background p-6"
        data-testid="error-boundary"
      >
        <div className="max-w-md w-full bg-card border rounded-lg shadow-sm p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                {stale ? 'This page has been updated' : 'Something went wrong'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {stale
                  ? 'Your browser is still holding the previous version of HAL. Reload to pick up the new one.'
                  : 'The page hit an unexpected error.'}
              </p>
            </div>
          </div>

          {!stale && this.state.error?.message && (
            <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap text-muted-foreground">
              {this.state.error.message}
            </pre>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={this.handleDismiss}
              data-testid="error-boundary-dismiss"
            >
              Dismiss
            </Button>
            <Button
              size="sm"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Reload Page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
